import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

export default function AutosizeTextArea({
  value = '',
  onChange,
  placeholder = '',
  minRows = 3,
  maxRows = null,
  disabled = false,
  className = '',
  ...rest
}) {
  const textareaRef = useRef(null);

  const adjustHeight = () => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    // Reset height to auto to get the correct scrollHeight
    textarea.style.height = 'auto';

    // Calculate new height
    let newHeight = textarea.scrollHeight;

    // Apply max height if maxRows is set
    if (maxRows) {
      const lineHeight = parseInt(getComputedStyle(textarea).lineHeight);
      const maxHeight = lineHeight * maxRows;
      newHeight = Math.min(newHeight, maxHeight);
    }

    textarea.style.height = `${newHeight}px`;
  };

  // Adjust height on mount and when value changes
  useEffect(() => {
    adjustHeight();
  }, [value]);

  const handleChange = (e) => {
    onChange(e);
    adjustHeight();
  };

  const textareaClasses = classNames(
    'w-full px-3 py-2 border border-gray-300 rounded-lg',
    'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500',
    'transition-all duration-200',
    'resize-none overflow-hidden',
    'font-sans text-base',
    {
      'bg-gray-100 cursor-not-allowed opacity-60': disabled,
      'bg-white': !disabled,
    },
    className,
  );

  return (
    <textarea
      ref={textareaRef}
      value={value}
      onChange={handleChange}
      placeholder={placeholder}
      rows={minRows}
      disabled={disabled}
      className={textareaClasses}
      {...rest}
    />
  );
}

AutosizeTextArea.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  minRows: PropTypes.number,
  maxRows: PropTypes.number,
  disabled: PropTypes.bool,
  className: PropTypes.string,
};

